import React, {useEffect, useRef, useState} from 'react';
import {Grid3x3Icon} from 'lucide-react';
import CanvasController from './CanvasController.ts';
import {gridSizes} from './canvasGrid.ts';
import {ToolButton} from '../ds/ToolFrame.tsx';

interface GridMenuProps {
    controller: CanvasController;
}

/*
 * The grid button and its menu: show the grid, snap moved objects to it,
 * and how far apart its lines are. The settings are the viewer's own
 * (kept per browser), not the board's.
 */
const GridMenu: React.FC<GridMenuProps> = ({controller}) => {
    const [open, setOpen] = useState(false);
    const container = useRef<HTMLDivElement>(null);
    const grid = controller.grid;

    useEffect(() => {
        if (!open) {
            return;
        }

        const closeOnOutsideClick = (event: MouseEvent) => {
            if (!container.current?.contains(event.target as Node)) {
                setOpen(false);
            }
        };
        const closeOnEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setOpen(false);
            }
        };

        document.addEventListener('mousedown', closeOnOutsideClick);
        document.addEventListener('keydown', closeOnEscape);
        return () => {
            document.removeEventListener('mousedown', closeOnOutsideClick);
            document.removeEventListener('keydown', closeOnEscape);
        };
    }, [open]);

    return (
        <div className="mx-grid-menu" ref={container}>
            <ToolButton
                label="Grid"
                icon={Grid3x3Icon}
                selected={grid.show || grid.snap}
                hasMenu
                aria-expanded={open}
                onClick={() => setOpen(o => !o)}
            />
            {open && (
                <div
                    className="mx-grid-menu-panel"
                    role="group"
                    aria-label="Grid"
                >
                    <label className="mx-grid-menu-row">
                        <input
                            type="checkbox"
                            className="form-check-input"
                            checked={grid.show}
                            onChange={e =>
                                controller.setGrid({show: e.target.checked})
                            }
                        />
                        Show grid
                    </label>
                    <label className="mx-grid-menu-row">
                        <input
                            type="checkbox"
                            className="form-check-input"
                            checked={grid.snap}
                            onChange={e =>
                                controller.setGrid({snap: e.target.checked})
                            }
                        />
                        Snap to grid
                    </label>
                    <label className="mx-grid-menu-row">
                        Spacing
                        <select
                            className="form-select form-select-sm mx-toolbar-select mx-toolbar-select-narrow"
                            aria-label="Grid spacing"
                            value={grid.size}
                            onChange={e =>
                                controller.setGrid({
                                    size: parseInt(e.target.value)
                                })
                            }
                        >
                            {gridSizes.map(size => (
                                <option key={size} value={size}>
                                    {size}
                                </option>
                            ))}
                        </select>
                    </label>
                </div>
            )}
        </div>
    );
};

export default GridMenu;
